// redux/slices/inventorySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const api = () => getServerUrl().replace(/\/$/, "");

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback ||
  "Request failed";

const axiosRequest = async ({
  url,
  method = "GET",
  data,
  headers = {},
  responseType,
}) => {
  const jwt = await getJwtToken();

  const config = {
    url: `${api()}${url}`,
    method,
    headers: {
      ...(data instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...headers,
    },
    responseType,
  };

  // важно: не отправляем data:null на DELETE
  if (data !== undefined) {
    config.data = data;
  }

  return axios(config);
};

const buildFormData = (obj = {}) => {
  const formData = new FormData();

  Object.entries(obj).forEach(([key, value]) => {
    if (key === "image" && value) {
      if (value instanceof File || value instanceof Blob) {
        formData.append("image", value);
      } else if (value.file) {
        formData.append("image", value.file);
      }
    } else if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else if (value === null || value === undefined) {
      formData.append(key, "");
    } else {
      formData.append(key, String(value));
    }
  });

  return formData;
};

/* ===================== PRODUCTS ===================== */

export const fetchProducts = createAsyncThunk(
  "inventory/fetchProducts",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const { data } = await axiosRequest({
        url: `/products/${uid}`,
      });

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Fetch Products failed"),
      });
    }
  }
);

export const createProduct = createAsyncThunk(
  "inventory/createProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const formData = buildFormData(productData);

      const { data } = await axiosRequest({
        url: "/products",
        method: "POST",
        data: formData,
      });

      return data?.product || data;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Create Product failed"),
      });
    }
  }
);

export const updateProduct = createAsyncThunk(
  "inventory/updateProduct",
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const formData = buildFormData(productData);

      const { data } = await axiosRequest({
        url: `/products/${id}`,
        method: "PUT",
        data: formData,
      });

      return data?.product || data;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Update Product failed"),
      });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "inventory/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      if (!productId) throw new Error("ID не передан");

      await axiosRequest({
        url: `/products/${productId}`,
        method: "DELETE",
      });

      return productId;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Delete Product failed"),
      });
    }
  }
);

/* ===================== CATEGORIES ===================== */

export const fetchProductCategories = createAsyncThunk(
  "inventory/fetchProductCategories",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const { data } = await axiosRequest({
        url: `/products/categories/${uid}`,
      });

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Fetch Product Categories failed"),
      });
    }
  }
);

export const addProductCategory = createAsyncThunk(
  "inventory/addProductCategory",
  async (categoryData, { rejectWithValue }) => {
    try {
      const { data } = await axiosRequest({
        url: "/products/categories",
        method: "POST",
        data: categoryData,
      });

      return data?.category || data;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Add Product Category failed"),
      });
    }
  }
);

export const updateProductCategory = createAsyncThunk(
  "inventory/updateProductCategory",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const { data } = await axiosRequest({
        url: `/products/categories/${id}`,
        method: "PUT",
        data: updatedData,
      });

      return data?.category || data;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Update Product Category failed"),
      });
    }
  }
);

export const deleteProductCategory = createAsyncThunk(
  "inventory/deleteProductCategory",
  async (categoryId, { rejectWithValue }) => {
    try {
      if (!categoryId) throw new Error("ID не передан");

      await axiosRequest({
        url: `/products/categories/${categoryId}`,
        method: "DELETE",
      });

      return categoryId;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Delete Product Category failed"),
      });
    }
  }
);

/* ===================== PRODUCT SETTINGS ===================== */

export const fetchProductSettings = createAsyncThunk(
  "inventory/fetchProductSettings",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const { data } = await axiosRequest({
        url: `/products/${uid}/product-settings`,
      });

      return data || {};
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Fetch Product Settings failed"),
      });
    }
  }
);

export const saveProductSettings = createAsyncThunk(
  "inventory/saveProductSettings",
  async ({ uid, settings, delivery }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const body = settings ? { settings } : { delivery: delivery ?? "" };

      const { data } = await axiosRequest({
        url: `/products/${uid}/product-settings`,
        method: "PUT",
        data: body,
      });

      return data || body;
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Save Product Settings failed"),
      });
    }
  }
);

/* ===================== EXPORT ===================== */

export const exportInventoryToExcel = createAsyncThunk(
  "inventory/exportInventoryToExcel",
  async ({ uid, language }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const lang = encodeURIComponent(language || "ru");

      const response = await axiosRequest({
        url: `/products/${uid}/export?lang=${lang}`,
        method: "GET",
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type:
          response.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `inventory_${uid}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return rejectWithValue({
        message: getErrorMessage(error, "Export Inventory failed"),
      });
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  products: [],
  categories: [],
  productSettings: {},
  status: "idle",
  error: null,
  loading: false,
};

/* ===================== SLICE ===================== */

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    clearInventoryError: (state) => {
      state.error = null;
    },
    resetInventoryStatus: (state) => {
      state.status = "idle";
      state.loading = false;
      state.error = null;
    },
    clearInventory: (state) => {
      state.products = [];
      state.categories = [];
      state.productSettings = {};
      state.status = "idle";
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload || [];
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error?.message;
      })

      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.products.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message;
      })

      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload;
        const index = state.products.findIndex(
          (item) => String(item.id) === String(updated?.id)
        );

        if (index !== -1) state.products[index] = updated;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message;
      })

      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(
          (item) => String(item.id) !== String(action.payload)
        );
      })

      .addCase(fetchProductCategories.fulfilled, (state, action) => {
        state.categories = action.payload || [];
      })

      .addCase(addProductCategory.fulfilled, (state, action) => {
        if (action.payload) state.categories.push(action.payload);
      })

      .addCase(updateProductCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductCategory.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload;
        const index = state.categories.findIndex(
          (item) => String(item.id) === String(updated?.id)
        );

        if (index !== -1) state.categories[index] = updated;
      })
      .addCase(updateProductCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message;
      })

      .addCase(deleteProductCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (item) => String(item.id) !== String(action.payload)
        );
      })

      .addCase(fetchProductSettings.fulfilled, (state, action) => {
        state.productSettings = action.payload || {};
      })

      .addCase(saveProductSettings.fulfilled, (state, action) => {
        state.productSettings = {
          ...state.productSettings,
          ...(action.payload || {}),
        };
      })

      .addCase(exportInventoryToExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportInventoryToExcel.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportInventoryToExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error?.message;
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearInventoryError,
  resetInventoryStatus,
  clearInventory,
} = inventorySlice.actions;

/* ===================== SELECTORS ===================== */

export const selectInventoryState = (state) => state.inventory;
export const selectProducts = (state) => state.inventory?.products || [];
export const selectProductCategories = (state) =>
  state.inventory?.categories || [];
export const selectProductSettings = (state) =>
  state.inventory?.productSettings || {};
export const selectInventoryStatus = (state) =>
  state.inventory?.status || "idle";
export const selectInventoryLoading = (state) =>
  !!state.inventory?.loading;
export const selectInventoryError = (state) =>
  state.inventory?.error || null;

export default inventorySlice.reducer;